import { defineStore, storeToRefs } from "pinia";
import { computed, ref, watch } from "vue";

import { useEditManager } from "@/composables/edit-manager";
import { api, type types } from "@/lib/openapi";
import { onEntityChange } from "@/stores/cache-invalidation";
import { EntityType, useCurrentEntityManager } from "@/stores/entity-store";
import { useReleaseStore } from "@/stores/release-store";

export type ProcessElement = types.ProcessElement;
export type ProcessElementType = types.ProcessElementType;

// Different from the 'process_element', as it fetches a list of process_elements for you for the
// currently selected process.
export const useProcessElementsStore = defineStore("process_elements", () => {
    const isFetching = ref(false);
    const {
        process,
        stek,
        isFetching: isEntityBeingFetched,
    } = useCurrentEntityManager();
    const releaseStore = useReleaseStore();
    const { isEditing } = useEditManager();
    const processElements = ref<ProcessElement[] | undefined>(undefined);
    const archivedProcessElements = ref<ProcessElement[] | undefined>(
        undefined
    );

    const controller = ref(new AbortController());

    /** The element types this list holds, as EntityType wire values. */
    const ELEMENT_ENTITY_TYPES: readonly string[] = [
        EntityType.TASK,
        EntityType.GATEWAY,
        EntityType.EVENT,
        EntityType.FLOW,
    ];

    // Refetch whenever ANY change to one of this process's elements is synced.
    //
    // Keyed by entity rather than attribute, per cache-invalidation.ts: this
    // used to whitelist `process_definition` alone, so saving an element's
    // description left the list untouched. That matters because the
    // description is a server-minted artefact — the first save creates it, so
    // the `description` link only exists after a refetch — and everything that
    // reads this list (the element drawer, the hover card, the description
    // layer badge) kept rendering the pre-save state until a reload, which
    // read as the edit having been discarded.
    //
    // The process itself stays narrowed to `process_definition`: that is the
    // only process attribute that changes this list, because the backend
    // re-derives the elements from the diagram on definition sync. Widening it
    // would refetch on every keystroke-burst of a process description for
    // nothing.
    onEntityChange(
        (change) =>
            ELEMENT_ENTITY_TYPES.includes(change.entityType) ||
            (change.entityType === EntityType.PROCESS &&
                change.attribute === "process_definition"),
        () => {
            if (stek.value) {
                refresh();
            }
        }
    );

    async function refresh() {
        // Abort any previously running requests and create a new controller
        controller.value.abort();
        controller.value = new AbortController();

        if (!process.value) {
            processElements.value = [];
            archivedProcessElements.value = [];
            return;
        }

        isFetching.value = true;
        try {
            const { response, data = [] } =
                await api.startProcessProcessElementMgtGetProcessElementsByProcessStartPost(
                    {
                        body: {
                            process_id: process.value.identity,
                            include_archived: true,
                        },
                        signal: controller.value.signal,
                    }
                );
            if (response.ok) {
                processElements.value = data.filter(
                    (process_element) => !process_element.is_archived
                );
                archivedProcessElements.value = data.filter(
                    (process_element) => process_element.is_archived
                );
            }
        } catch (e) {
            if (e instanceof DOMException && e.name === "AbortError") {
                // Request was aborted by the AbortController.
                return;
            } else {
                throw e;
            }
        } finally {
            isFetching.value = false;
        }
    }

    // Load process_elements when process has changed or definition is modidied.
    watch(
        [
            () => process.value?.identity,
            isEditing,
            () => releaseStore.activeRevision,
        ],
        (
            [identity, newIsEditing, revision],
            [oldIdentity, wasEditing, oldRevision]
        ) => {
            if (
                identity !== oldIdentity ||
                (newIsEditing === false && wasEditing === true) ||
                revision !== oldRevision
            ) {
                refresh();
            }
        },
        { immediate: true }
    );

    return {
        processElements,
        archivedProcessElements,
        isFetching: computed(
            () => isFetching.value || isEntityBeingFetched.value
        ),
        refresh,
    };
});

export const useProcessElementsManager = () =>
    storeToRefs(useProcessElementsStore());

export type ProcessTaskDisplayMeta = {
    identity: string;
    name?: string | null;
    bpmn_id?: string | null;
};

/** Human-facing label for a ProcessTask: name, else BPMN id, else identity. */
export const getProcessTaskDisplayName = ({
    identity,
    name,
    bpmn_id,
}: ProcessTaskDisplayMeta): string => name || bpmn_id || identity;

/** Id -> display-name lookup over a list of ProcessTasks (unknown ids echo back). */
export const createProcessTaskNameLookup = (
    tasks: Pick<ProcessTaskDisplayMeta, "identity" | "name">[]
) => {
    const taskNames = new Map(
        tasks.map((task) => [task.identity, task.name || task.identity])
    );

    return (taskId: string): string => taskNames.get(taskId) ?? taskId;
};
